import { collection, doc, getDoc, getDocs, query, where, addDoc, updateDoc, serverTimestamp, Timestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

export interface AttendanceRecord {
  studentId: string;
  signInTime: Date | null;
  signOutTime: Date | null;
  signInName: string;
  signOutName: string;
}

export interface Attendance {
  id: string;
  courseId: string;
  programId: string;
  date: Date;
  records: Record<string, AttendanceRecord>;
  createdById?: string;
  createdByName?: string;
}

// 根据考勤ID获取考勤记录
export const getAttendance = async (attendanceId: string): Promise<Attendance | null> => {
  try {
    const attendanceRef = doc(db, 'attendance', attendanceId);
    const attendanceDoc = await getDoc(attendanceRef);
    
    if (!attendanceDoc.exists()) {
      return null;
    }

    const attendanceData = attendanceDoc.data();
    console.log({attendanceData});
    
    // 转换记录格式
    const records: Record<string, AttendanceRecord> = {};
    
    if (attendanceData.records && Array.isArray(attendanceData.records)) {
      attendanceData.records.forEach((record: any) => {
        if (record.student && record.student.id) {
          records[record.student.id] = {
            studentId: record.student.id,
            signInTime: record.signInTime ? record.signInTime.toDate() : null,
            signOutTime: record.signOutTime ? record.signOutTime.toDate() : null,
            signInName: record.signInName || '',
            signOutName: record.signOutName || ''
          };
        }
      });
    }

    // 从course引用中获取courseId
    const courseId = attendanceData.course?.id || '';
    // 从program引用中获取programId
    const programId = attendanceData.program?.id || '';
    // 创建人
    const createdById = attendanceData.createdBy?.id || '';
    const createdByName = attendanceData.createdByName || '';

    return {
      id: attendanceDoc.id,
      courseId,
      programId,
      date: attendanceData.schedule.toDate(),
      records,
      createdById,
      createdByName
    };
  } catch (error) {
    console.error('获取考勤记录失败:', error);
    throw error;
  }
};

// 创建新的考勤记录
export const createAttendance = async (
  courseId: string,
  programId: string,
  date: Date,
  students: string[],
  creator?: { id: string; name: string }
): Promise<Attendance> => {
  try {
    const courseRef = doc(db, 'courses', courseId);
    const programRef = doc(db, 'programs', programId);
    
    // 创建考勤记录数组
    const records = students.map(studentId => ({
      student: doc(db, 'registrations', studentId),
      signInTime: null,
      signOutTime: null,
      signInName: '',
      signOutName: ''
    }));

    const attendanceData: any = {
      course: courseRef,
      program: programRef,
      schedule: date,
      records: records,
      deleted: false,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    };

    // 记录创建人（可选）
    if (creator?.id) {
      attendanceData.createdBy = doc(db, 'users', creator.id);
      attendanceData.createdByName = creator.name || '';
    }

    const docRef = await addDoc(collection(db, 'attendance'), attendanceData);
    
    // 转换为返回格式
    const studentRecords: Record<string, AttendanceRecord> = {};
    students.forEach(studentId => {
      studentRecords[studentId] = {
        studentId,
        signInTime: null,
        signOutTime: null,
        signInName: '',
        signOutName: ''
      };
    });

    return {
      id: docRef.id,
      courseId,
      programId,
      date,
      records: studentRecords,
      createdById: creator?.id,
      createdByName: creator?.name || ''
    };
  } catch (error) {
    console.error('创建考勤记录失败:', error);
    throw error;
  }
};

// 更新学生签到状态
export const updateStudentSignIn = async (
  attendanceId: string, 
  studentId: string, 
  signInName: string
): Promise<void> => {
  try {
    const attendanceRef = doc(db, 'attendance', attendanceId);
    const attendanceDoc = await getDoc(attendanceRef);
    
    if (!attendanceDoc.exists()) {
      throw new Error('考勤记录不存在');
    }
    
    const attendanceData = attendanceDoc.data();
    const records = attendanceData.records || [];
    console.log({records},studentId);
    // 查找并更新对应学生的记录
    let updated = false;
    const updatedRecords = records.map((record: any) => {
      if (record.student && record.student.id === studentId) {
        updated = true;
        return {
          ...record,
          signInTime: Timestamp.now(),
          signInName
        };
      }
      return record;
    });
    if(!updated) {
      updatedRecords.push({
        student: doc(db, 'registrations', studentId),
        signInTime: Timestamp.now(),
        signInName,
        signOutTime: null,
        signOutName: ''
      });
    }
    
    // 更新文档
    await updateDoc(attendanceRef, {
      records: updatedRecords,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('更新签到状态失败:', error);
    throw error;
  }
};

// 更新学生签退状态
export const updateStudentSignOut = async (
  attendanceId: string, 
  studentId: string, 
  signOutName: string
): Promise<void> => {
  try {
    const attendanceRef = doc(db, 'attendance', attendanceId);
    const attendanceDoc = await getDoc(attendanceRef);
    
    if (!attendanceDoc.exists()) {
      throw new Error('考勤记录不存在');
    }
    
    const attendanceData = attendanceDoc.data();
    const records = attendanceData.records || [];
      
    // 查找并更新对应学生的记录
    const updatedRecords = records.map((record: any) => {
      if (record.student && record.student.id === studentId) {
        return {
          ...record,
          signOutTime: Timestamp.now(),
          signOutName
        };
      }
      return record;
    });
    
    // 更新文档
    await updateDoc(attendanceRef, {
      records: updatedRecords,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('更新签退状态失败:', error);
    throw error;
  }
};

// 批量签到指定的学生
export const batchSignInStudents = async (
  attendanceId: string,
  signInName: string,
  selectedStudentIds?: string[]
): Promise<void> => {
  try {
    const attendanceRef = doc(db, 'attendance', attendanceId);
    const attendanceDoc = await getDoc(attendanceRef);
    
    if (!attendanceDoc.exists()) {
      throw new Error('考勤记录不存在');
    }
    
    const attendanceData = attendanceDoc.data();
    const records = attendanceData.records || [];
    
    // 如果没有指定学生列表，则为所有未签到的学生签到（保持向后兼容）
    // 如果指定了学生列表，则只为指定的学生签到
    const updatedRecords = records.map((record: any) => {
      if (record.student && !record.signInTime) {
        // 如果没有指定学生列表，为所有未签到学生签到
        if (!selectedStudentIds || selectedStudentIds.length === 0) {
          return {
            ...record,
            signInTime: Timestamp.now(),
            signInName
          };
        }
        // 如果指定了学生列表，只为列表中的学生签到
        else if (selectedStudentIds.includes(record.student.id)) {
          return {
            ...record,
            signInTime: Timestamp.now(),
            signInName
          };
        }
      }
      return record;
    });

    // 处理新增的学生记录（如果学生不在原记录中但在选择列表中）
    if (selectedStudentIds && selectedStudentIds.length > 0) {
      const existingStudentIds = records.map((record: any) => record.student?.id).filter(Boolean);
      const newStudentIds = selectedStudentIds.filter(id => !existingStudentIds.includes(id));
      
      // 为新学生添加记录
      const newRecords = newStudentIds.map(studentId => ({
        student: doc(db, 'registrations', studentId),
        signInTime: Timestamp.now(),
        signInName,
        signOutTime: null,
        signOutName: ''
      }));
      
      updatedRecords.push(...newRecords);
    }
    
    // 更新文档
    await updateDoc(attendanceRef, {
      records: updatedRecords,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('批量签到失败:', error);
    throw error;
  }
};

// 软删除考勤记录
export const deleteAttendance = async (attendanceId: string): Promise<void> => {
  try {
    const attendanceRef = doc(db, 'attendance', attendanceId);

    // 验证考勤记录是否存在
    const attendanceDoc = await getDoc(attendanceRef);
    if (!attendanceDoc.exists()) {
      throw new Error('考勤记录不存在');
    }

    // 软删除：设置deleted字段为true
    await updateDoc(attendanceRef, {
      deleted: true,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('软删除考勤记录失败:', error);
    throw error;
  }
};

// 恢复软删除的考勤记录
export const restoreAttendance = async (attendanceId: string): Promise<void> => {
  try {
    const attendanceRef = doc(db, 'attendance', attendanceId);

    // 验证考勤记录是否存在
    const attendanceDoc = await getDoc(attendanceRef);
    if (!attendanceDoc.exists()) {
      throw new Error('考勤记录不存在');
    }

    // 恢复：设置deleted字段为false
    await updateDoc(attendanceRef, {
      deleted: false,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('恢复考勤记录失败:', error);
    throw error;
  }
}; 